import type { PathWithPlaceholders as PathValue } from '#/routes';

type PathParamsToPath<PathValue, Params> =
  PathValue extends `${infer Prefix}:${infer Param}/${infer Suffix}`
    ? Param extends keyof Params
      ? Params[Param] extends string | number
        ? `${Prefix}${Params[Param]}${PathParamsToPath<`/${Suffix}`, Params>}`
        : never
      : never
    : PathValue extends `${infer Prefix}:${infer Param}`
      ? Param extends keyof Params
        ? Params[Param] extends string | number
          ? `${Prefix}${Params[Param]}`
          : never
        : never
      : PathValue;

export type PathParams<Path extends string> =
  Path extends `${infer _}:${infer Param}/${infer Suffix}`
    ? {
        [K in Param | keyof PathParams<`/${Suffix}`>]:
          | string
          | number
          | null
          | undefined;
      }
    : Path extends `${infer _}:${infer Param}`
      ? { [K in Param]: string | null | undefined }
      : Record<string, never>;

type SearchParams =
  | Record<string, string | boolean | number | null | undefined>
  | undefined;
type PathParamValue = string | number | null | undefined;
type RuntimePathParams = Record<string, PathParamValue>;
type StaticPath = Exclude<PathValue, `${string}:${string}`>;
type ParameterizedPath = Extract<PathValue, `${string}:${string}`>;

const pathPlaceholderPattern = /:[a-zA-Z]+/g;

type QueryStringParamsToString<Params extends SearchParams> = {
  [K in keyof Params]: `?${string}=${string}`;
}[keyof Params];

type GenerateURL<
  Path extends PathValue,
  Params extends PathParams<Path>,
  Search extends SearchParams = Record<string, never>,
> =
  Search extends Record<string, never>
    ? `${PathParamsToPath<Path, Params>}${string extends keyof Search
        ? ''
        : '?'}${QueryStringParamsToString<Search>}`
    : PathParamsToPath<Path, Params>;

/**
 * Generates a URL based on the provided path, parameters, and search parameters.
 *
 * @template Path - The value representing the path.
 * @template Params - The type representing the path parameters.
 * @template Search - The type representing the search parameters.
 * @param {Path} path - The path value.
 * @param {Params | null | undefined} params - The path parameters.
 * @param {Search} [search] - The search parameters.
 * @throws {Error} When an invalid parameter or value is provided.
 *
 * @description
 * Generates a URL by replacing placeholders in the provided path with values from the `params` object. The search parameters are appended as a query string. An error is thrown if a parameter is invalid.
 */
export function generateUrl<Path extends StaticPath>(
  path: Path,
  params?: PathParams<Path> | null,
  search?: SearchParams,
): GenerateURL<Path, PathParams<Path>, SearchParams>;
export function generateUrl<Path extends ParameterizedPath>(
  path: Path,
  params: PathParams<Path> | null | undefined,
  search?: SearchParams,
): GenerateURL<Path, PathParams<Path>, SearchParams>;
export function generateUrl<Path extends PathValue>(
  path: Path,
  params?: PathParams<Path> | null,
  search?: SearchParams,
): GenerateURL<Path, PathParams<Path>, SearchParams> {
  const url = replacePathParams(path, params as RuntimePathParams | null);
  const urlWithSearch = appendSearchParams(url, search);
  const normalizedUrl = urlWithSearch.replace(/([^:]\/)\/+/g, '$1');

  return normalizedUrl as GenerateURL<Path, PathParams<Path>, SearchParams>;
}

function replacePathParams(
  path: string,
  params: RuntimePathParams | null | undefined,
): string {
  if (!params) return path;

  const paramKeys = Object.keys(params);
  const placeholderCount = path.match(pathPlaceholderPattern)?.length ?? 0;
  if (placeholderCount > 0 && placeholderCount !== paramKeys.length) {
    throw new Error(
      'Mismatch between number of parameters and placeholders in the path.',
    );
  }

  return paramKeys.reduce((url, key) => {
    const placeholder = `:${key}`;
    if (!path.includes(placeholder)) {
      throw new Error(
        `Invalid parameter key "${key}". It does not exist in the path.`,
      );
    }

    const value = params[key];
    warnAboutNullishPathParam(key, value);
    return url.replace(placeholder, String(value));
  }, path);
}

function warnAboutNullishPathParam(key: string, value: PathParamValue): void {
  if (
    (value !== null && value !== undefined) ||
    import.meta.env.MODE === 'test'
  ) {
    return;
  }

  console.warn(
    `generateUrl: Parameter "${key}" is ${value === null ? 'null' : 'undefined'}.`,
  );
}

function appendSearchParams(path: string, search: SearchParams): string {
  if (!search) return path;

  const queryString = Object.entries(search)
    .filter((entry): entry is [string, string | boolean | number] => {
      const value = entry[1];
      return value !== null && value !== undefined;
    })
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value.toString())}`,
    )
    .join('&');

  return queryString ? `${path}?${queryString}` : path;
}
